import { z } from 'zod';

export const appId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/);
export const appReference = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[^\x00-\x20/\\]+$/);
export const appDigest = z.string().regex(/^[a-f0-9]{64}$/);
export const appName = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/);
export const appText = (max) => z.string().min(1).max(max);
export const appVersion = z.number().int().nonnegative().safe();
export const uniqueBy = (key) => (items) =>
  new Set(items.map((item) => item[key])).size === items.length;

function validSchemaNode(node) {
  if (!node || typeof node !== 'object') return true;
  if (Object.hasOwn(node, '$ref') && !String(node.$ref).startsWith('#/')) return false;
  return !Object.keys(node).some((key) => ['__proto__', 'constructor', 'prototype'].includes(key));
}

// An inline argument schema is data, never an external schema fetch or a resolver.
const schemaRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);
function inlineSchema(value) {
  if (typeof value === 'boolean') return true;
  if (!schemaRecord(value)) return false;
  const stack = [[value, 0]];
  let nodes = 0;
  while (stack.length) {
    const [node, depth] = stack.pop();
    if (++nodes > 2048 || depth > 16) return false;
    if (!node || typeof node !== 'object') continue;
    if (!validSchemaNode(node)) return false;
    for (const child of Object.values(node)) stack.push([child, depth + 1]);
  }
  return JSON.stringify(value).length <= 65536;
}

export const inlineJsonSchema = z
  .unknown()
  .refine(inlineSchema, 'Use bounded inline JSON Schema')
  .pipe(z.json());
export const declarationList = (schema, max = 100) =>
  z
    .array(schema)
    .max(max)
    .refine(uniqueBy('id'), 'Declaration IDs must be unique')
    .refine(uniqueBy('name'), 'Declaration names must be unique');

// Public sources retain issuer provenance. An owner connection is only an
// opaque selector; its current company audience and access are checked at runtime.
export const sourceBindingDeclarationSchema = z.union([
  z.strictObject({
    connectionMode: z.enum(['owner', 'viewer']).default('owner'),
    queryTemplateDigest: appDigest,
    companyPolicyVersion: appText(128),
    disclosurePolicyRef: appReference,
  }),
  z.strictObject({
    connectionMode: z.literal('owner').default('owner'),
    ownerConnectionRef: appReference,
    queryTemplateDigest: appDigest.optional(),
    companyPolicyVersion: appText(128).optional(),
    disclosurePolicyRef: appReference.optional(),
  }),
]);

// PUB accepts a creator declaration; SES validates current authority at execution.
// The owner reference is opaque. Its presence is not a credential or a permission.
export const sourceDeclarationSchema = z
  .object({
    id: appId,
    name: appName,
    systemId: appId,
    binding: sourceBindingDeclarationSchema,
    argumentsSchema: inlineJsonSchema,
  })
  .strict();
